/** @module @liquidefy/code-editor/circuits/diff-review @version 1.0.0 @license LQD Research LLC */

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import { buildHealthReport, emptyStats, recordSuccess, recordError, healthFromStats, formatStatsDetails } from './circuit-types.js';

import type {
  Range,
  EditorResult,
  DiffAlgorithm,
  DiffResult,
  DiffStats,
  DiffHunk,
  DiffLine,
  ThreeWayMergeState,
  ReviewThreadId,
  ReviewCommentId,
  ReviewThread,
  ReviewComment,
  ReviewState,
  DiffId,
} from '../code-editor-types.js';

import { ok, err, generateId } from '../code-editor.js';

import { computeDiff, computeStats, myersDiff, patienceDiff, histogramDiff, normalizeHunks } from '../diff/compute.js';
import { formatUnifiedDiff, prepareSideBySide } from '../diff/render.js';
import { threeWayMerge, resolveConflict as resolveConflictInMerge, allResolved, resolveProgress } from '../diff/merge.js';
import { createReviewState, addComment as addReviewComment, resolveThread as resolveReviewThread, addSuggestion as addReviewSuggestion, setApprovalState, getReviewSummary } from '../diff/review.js';


// ═══════════════════════════════════════════════════════════════════════════════
// §1 CONFIG & HANDLE INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

export interface DiffReviewConfig {
  readonly originalContent: string;
  readonly modifiedContent: string;
  readonly algorithm?: DiffAlgorithm;
}

export interface ReviewSummary {
  readonly threads: number;
  readonly resolved: number;
  readonly pending: number;
}

export interface SideBySideResult {
  readonly left: SideBySideLine[];
  readonly right: SideBySideLine[];
}

export interface SideBySideLine {
  readonly lineNumber: number | null;
  readonly content: string;
  readonly type: 'context' | 'add' | 'delete' | 'equal' | 'gap';
}

export interface DiffReviewHandle extends CircuitHandle {
  getDiff(): DiffResult;
  getStats(): DiffStats;
  formatUnified(contextLines?: number): string;
  formatSideBySide(): SideBySideResult;
  addComment(fileUri: string, range: Range, authorId: string, body: string): EditorResult<ReviewState>;
  resolveThread(threadId: ReviewThreadId): EditorResult<ReviewState>;
  addSuggestion(range: Range, replacement: string): EditorResult<ReviewSuggestionState>;
  getReviewSummary(): ReviewSummary;
  approve(userId: string): EditorResult<ReviewState>;
  requestChanges(userId: string, comment: string): EditorResult<ReviewState>;
  getMergeState(base: string): EditorResult<ThreeWayMergeState>;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

export interface ReviewSuggestionState {
  readonly id: string;
  readonly range: Range;
  readonly replacement: string;
  readonly appliedAt: number;
}


// ═══════════════════════════════════════════════════════════════════════════════
// §2 INTERNAL STATE
// ═══════════════════════════════════════════════════════════════════════════════

interface InternalDiffState {
  diff: DiffResult;
  review: ReturnType<typeof createReviewState>;
  suggestions: ReviewSuggestionState[];
  approvals: Map<string, 'approved' | 'changes-requested'>;
  mergeState: ThreeWayMergeState | null;
  originalContent: string;
  modifiedContent: string;
  algorithm: DiffAlgorithm;
}


// ═══════════════════════════════════════════════════════════════════════════════
// §3 PURE HELPERS — diff computation, formatting, side-by-side
// ═══════════════════════════════════════════════════════════════════════════════

function computeDiffResult(original: string, modified: string, algorithm: DiffAlgorithm): DiffResult {
  const originalLines = original.split('\n');
  const modifiedLines = modified.split('\n');

  let hunks: DiffHunk[];
  switch (algorithm) {
    case 'patience':
      hunks = patienceDiff(originalLines, modifiedLines);
      break;
    case 'histogram':
      hunks = histogramDiff(originalLines, modifiedLines);
      break;
    case 'myers':
    default:
      hunks = myersDiff(originalLines, modifiedLines);
      break;
  }

  hunks = normalizeHunks(hunks);
  const stats = computeStats(hunks);

  return {
    id: generateId('diff') as DiffId,
    algorithm,
    original,
    modified,
    hunks,
    stats,
  };
}

function buildSideBySide(diff: DiffResult): SideBySideResult {
  const left: SideBySideLine[] = [];
  const right: SideBySideLine[] = [];

  for (const hunk of diff.hunks) {
    const deletions: DiffLine[] = [];
    const additions: DiffLine[] = [];

    for (const line of hunk.lines) {
      switch (line.type) {
        case 'context':
        case 'equal': {
          // Flush paired changes
          flushPairedChanges(deletions, additions, left, right);
          deletions.length = 0;
          additions.length = 0;

          left.push({
            lineNumber: line.oldLineNumber ?? null,
            content: line.content,
            type: 'context',
          });
          right.push({
            lineNumber: line.newLineNumber ?? null,
            content: line.content,
            type: 'context',
          });
          break;
        }
        case 'delete':
          deletions.push(line);
          break;
        case 'add':
          additions.push(line);
          break;
      }
    }

    // Flush remaining
    flushPairedChanges(deletions, additions, left, right);
  }

  return { left, right };
}

function flushPairedChanges(
  deletions: DiffLine[],
  additions: DiffLine[],
  left: SideBySideLine[],
  right: SideBySideLine[],
): void {
  const maxLen = Math.max(deletions.length, additions.length);
  for (let i = 0; i < maxLen; i++) {
    if (i < deletions.length) {
      left.push({
        lineNumber: deletions[i]!.oldLineNumber ?? null,
        content: deletions[i]!.content,
        type: 'delete',
      });
    } else {
      left.push({ lineNumber: null, content: '', type: 'gap' });
    }

    if (i < additions.length) {
      right.push({
        lineNumber: additions[i]!.newLineNumber ?? null,
        content: additions[i]!.content,
        type: 'add',
      });
    } else {
      right.push({ lineNumber: null, content: '', type: 'gap' });
    }
  }
}

function computeReviewSummary(review: ReturnType<typeof createReviewState>): ReviewSummary {
  const summary = getReviewSummary(review);
  return {
    threads: summary.threadCount,
    resolved: summary.resolvedThreadCount,
    pending: summary.threadCount - summary.resolvedThreadCount,
  };
}


// ═══════════════════════════════════════════════════════════════════════════════
// §4 FACTORY — createDiffReview
// ═══════════════════════════════════════════════════════════════════════════════

export function createDiffReview(config: DiffReviewConfig): DiffReviewHandle {
  let circuitStats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // Compute initial diff
  const algorithm = config.algorithm ?? 'myers';
  const diff = computeDiffResult(config.originalContent, config.modifiedContent, algorithm);

  // Create review state
  const review = createReviewState(diff.id);

  const state: InternalDiffState = {
    diff,
    review,
    suggestions: [],
    approvals: new Map(),
    mergeState: null,
    originalContent: config.originalContent,
    modifiedContent: config.modifiedContent,
    algorithm,
  };

  return Object.freeze({
    name: 'diff-review',

    getDiff(): DiffResult {
      guardDestroyed();
      return state.diff;
    },

    getStats(): DiffStats {
      guardDestroyed();
      return state.diff.stats;
    },

    formatUnified(contextLines?: number): string {
      guardDestroyed();
      try {
        const result = formatUnifiedDiff(state.diff, contextLines);
        circuitStats = recordSuccess(circuitStats);
        return result;
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return `Error formatting diff: ${e instanceof Error ? e.message : String(e)}`;
      }
    },

    formatSideBySide(): SideBySideResult {
      guardDestroyed();
      try {
        const result = buildSideBySide(state.diff);
        circuitStats = recordSuccess(circuitStats);
        return result;
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return { left: [], right: [] };
      }
    },

    addComment(fileUri: string, range: Range, authorId: string, body: string): EditorResult<ReviewState> {
      guardDestroyed();
      try {
        const result = addReviewComment(state.review, {
          fileUri,
          range,
          initialComment: { authorId, body, reactions: {} },
        });
        if (!result.success) {
          circuitStats = recordError(circuitStats);
          return err(result.code, result.message);
        }
        state.review = result.value;
        circuitStats = recordSuccess(circuitStats);

        const reviewState: ReviewState = {
          status: 'pending',
          threads: state.review.base.threads,
        };
        return ok(reviewState, 'COMMENT_ADDED', 'Comment added successfully');
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('COMMENT_FAILED', `Failed to add comment: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    resolveThread(threadId: ReviewThreadId): EditorResult<ReviewState> {
      guardDestroyed();
      try {
        const result = resolveReviewThread(state.review, threadId);
        if (!result.success) {
          circuitStats = recordError(circuitStats);
          return err(result.code, result.message);
        }
        state.review = result.value;
        circuitStats = recordSuccess(circuitStats);

        const reviewState: ReviewState = {
          status: 'pending',
          threads: state.review.base.threads,
        };
        return ok(reviewState, 'THREAD_RESOLVED', 'Thread resolved successfully');
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('RESOLVE_FAILED', `Failed to resolve thread: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    addSuggestion(range: Range, replacement: string): EditorResult<ReviewSuggestionState> {
      guardDestroyed();
      try {
        const suggestion: ReviewSuggestionState = {
          id: generateId('sug'),
          range,
          replacement,
          appliedAt: Date.now(),
        };
        state.suggestions.push(suggestion);

        // Also add to review state
        const reviewResult = addReviewSuggestion(state.review, range, replacement);
        if (reviewResult.success) {
          state.review = reviewResult.value;
        }

        circuitStats = recordSuccess(circuitStats);
        return ok(suggestion, 'SUGGESTION_ADDED', 'Code suggestion added');
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('SUGGESTION_FAILED', `Failed to add suggestion: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    getReviewSummary(): ReviewSummary {
      guardDestroyed();
      return computeReviewSummary(state.review);
    },

    approve(userId: string): EditorResult<ReviewState> {
      guardDestroyed();
      try {
        state.approvals.set(userId, 'approved');
        const result = setApprovalState(state.review, userId, 'approved');
        if (result.success) {
          state.review = result.value;
        }

        circuitStats = recordSuccess(circuitStats);
        const reviewState: ReviewState = {
          status: 'approved',
          threads: state.review.base.threads,
        };
        return ok(reviewState, 'REVIEW_APPROVED', `Review approved by ${userId}`);
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('APPROVE_FAILED', `Failed to approve: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    requestChanges(userId: string, comment: string): EditorResult<ReviewState> {
      guardDestroyed();
      try {
        state.approvals.set(userId, 'changes-requested');
        const result = setApprovalState(state.review, userId, 'changes-requested');
        if (result.success) {
          state.review = result.value;
        }

        // Add the change request as a comment
        const commentResult = addReviewComment(state.review, {
          fileUri: 'review',
          range: { start: { line: 0, column: 0 }, end: { line: 0, column: 0 } },
          initialComment: { authorId: userId, body: `Changes requested: ${comment}`, reactions: {} },
        });
        if (commentResult.success) {
          state.review = commentResult.value;
        }

        circuitStats = recordSuccess(circuitStats);
        const reviewState: ReviewState = {
          status: 'changes-requested',
          threads: state.review.base.threads,
        };
        return ok(reviewState, 'CHANGES_REQUESTED', `Changes requested by ${userId}`);
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('REQUEST_CHANGES_FAILED', `Failed to request changes: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    getMergeState(base: string): EditorResult<ThreeWayMergeState> {
      guardDestroyed();
      try {
        const mergeResult = threeWayMerge(base, state.originalContent, state.modifiedContent);
        if (!mergeResult.success) {
          circuitStats = recordError(circuitStats);
          return err(mergeResult.code, mergeResult.message);
        }
        state.mergeState = mergeResult.value;
        circuitStats = recordSuccess(circuitStats);
        return ok(mergeResult.value, 'MERGE_COMPUTED', `Three-way merge computed, ${mergeResult.value.conflicts.length} conflict(s)`);
      } catch (e) {
        circuitStats = recordError(circuitStats);
        return err('MERGE_FAILED', `Failed to compute merge: ${e instanceof Error ? e.message : String(e)}`);
      }
    },

    stats(): CircuitStats {
      return { ...circuitStats, uptime: Date.now() - createdAt };
    },

    health(): CircuitHealthReport {
      if (destroyed) return buildHealthReport('diff-review', 'destroyed', 'Circuit destroyed', createdAt);
      const summary = computeReviewSummary(state.review);
      const mergeInfo = state.mergeState
        ? `, Merge conflicts: ${state.mergeState.conflicts.length}`
        : '';
      const details = `${formatStatsDetails(circuitStats)}, Threads: ${summary.threads}, Pending: ${summary.pending}${mergeInfo}`;
      return buildHealthReport('diff-review', healthFromStats(circuitStats), details, createdAt);
    },

    destroy(): void {
      destroyed = true;
      state.approvals.clear();
    },
  });
}
